import { useCallback, useEffect, useRef, useState } from 'react';
import { Calculator } from '@/components/icons';
import { cn } from '@/lib/utils';
import { Alignments } from '@/types/report';
import { TableCell as TableCellType } from '@/types/report-payload';
import type { MouseEvent, RefObject } from 'react';

type SelectionEdges = {
    top: boolean;
    right: boolean;
    bottom: boolean;
    left: boolean;
};

const SELECTED_RANGE_BORDER_COLOR = 'rgb(37 99 235)';

const getSelectionBoxShadow = (selectionEdges?: SelectionEdges) => {
    if (!selectionEdges) return undefined;

    const shadows = [
        selectionEdges.top ? `inset 0 2px 0 0 ${SELECTED_RANGE_BORDER_COLOR}` : null,
        selectionEdges.right ? `inset -2px 0 0 0 ${SELECTED_RANGE_BORDER_COLOR}` : null,
        selectionEdges.bottom ? `inset 0 -2px 0 0 ${SELECTED_RANGE_BORDER_COLOR}` : null,
        selectionEdges.left ? `inset 2px 0 0 0 ${SELECTED_RANGE_BORDER_COLOR}` : null,
    ].filter(Boolean);

    return shadows.length > 0 ? shadows.join(', ') : undefined;
};

interface TableCellProps {
    cell: TableCellType;
    isHeader: boolean;
    isSelected: boolean;
    selectionEdges?: SelectionEdges;
    /**
     * A linha-folha do cabeçalho — a última. Só ela ganha a alça de
     * redimensionar e o `data-col-index` de que o arrasto de coluna depende:
     * numa faixa de grupo o índice é o da primeira coluna do grupo.
     */
    isLeafHeader?: boolean;
    isEditing: boolean;
    disabled: boolean;
    textAlignment: Alignments;
    textColor?: string;
    backgroundColor?: string;
    onMouseDown: (event: MouseEvent) => void;
    onMouseEnter: (event: MouseEvent) => void;
    onClick: (event: MouseEvent) => void;
    onContextMenu: (event: MouseEvent) => void;
    onStartEdit: (event?: MouseEvent) => void;
    onCommit: (value: string, reason?: 'enter' | 'blur') => void;
    onCancel: () => void;
    editSeedValue?: string;
    editSeedKey?: number;
    editCursorPosition?: number | null;
    onOpenCalculator?: () => void;
    onResizeStart?: (event: MouseEvent) => void;
    onDraftChange?: (value: string) => void;
    colIndex?: number;
}

export function TableCell({
    cell,
    isHeader,
    isSelected,
    selectionEdges,
    isLeafHeader = true,
    isEditing,
    disabled,
    textAlignment,
    textColor,
    backgroundColor,
    onMouseDown,
    onMouseEnter,
    onClick,
    onContextMenu,
    onStartEdit,
    onCommit,
    onCancel,
    editSeedValue,
    editSeedKey,
    editCursorPosition,
    onOpenCalculator,
    onResizeStart,
    onDraftChange,
    colIndex,
}: TableCellProps) {
    const [draft, setDraft] = useState(cell.value || '');
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
    const cellRef = useRef<HTMLTableCellElement>(null);

    useEffect(() => {
        setDraft(cell.value || '');
    }, [cell.value]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
            if (editCursorPosition != null) {
                inputRef.current.setSelectionRange(editCursorPosition, editCursorPosition);
            }
        }
    }, [isEditing, editCursorPosition]);

    useEffect(() => {
        if (!isEditing) return;
        if (editSeedKey !== undefined) {
            setDraft(editSeedValue || '');
            return;
        }

        setDraft(cell.value || '');
    }, [cell.value, editSeedKey, editSeedValue, isEditing]);

    const alignmentClass =
        textAlignment === 'center'
            ? 'text-center'
            : textAlignment === 'right'
              ? 'text-right'
              : 'text-left';

    const Component = isHeader ? 'th' : 'td';
    const selectionBoxShadow =
        isSelected && !isEditing ? getSelectionBoxShadow(selectionEdges) : undefined;

    const handleDraftChange = (value: string) => {
        setDraft(value);
        onDraftChange?.(value);
    };

    const resizeTextarea = useCallback(() => {
        const el = inputRef.current;
        if (!el || el.tagName !== 'TEXTAREA') return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    }, []);

    // Auto-resize textarea whenever draft changes while editing
    useEffect(() => {
        if (isEditing) resizeTextarea();
    }, [isEditing, draft, resizeTextarea]);

    const handleSave = (reason?: 'enter' | 'blur') => {
        onCommit(draft, reason);
    };

    const handleCancel = () => {
        setDraft(cell.value || '');
        onCancel();
    };

    return (
        <>
            <Component
                className={cn(
                    'group relative border px-2 py-1 align-middle text-xs',
                    isHeader ? 'border-slate-300 font-semibold' : 'border-slate-200 font-normal',
                    isEditing ? 'z-[2] ring-[2.5px] ring-inset ring-blue-500' : '',
                    isSelected && !isEditing ? 'z-[1]' : '',
                    isSelected && !isEditing && !isHeader ? 'bg-blue-50/80' : 'bg-transparent',
                    !disabled && !isHeader && !isSelected ? 'hover:bg-blue-50' : '',
                    alignmentClass,
                )}
                style={{
                    backgroundColor: isHeader ? backgroundColor : undefined,
                    color: textColor,
                    boxShadow: selectionBoxShadow,
                }}
                ref={cellRef}
                data-col-index={isLeafHeader ? colIndex : undefined}
                onMouseDown={onMouseDown}
                onMouseEnter={onMouseEnter}
                onClick={onClick}
                onContextMenu={onContextMenu}
                onDoubleClick={(event) => {
                    if (disabled) return;
                    onStartEdit(event);
                }}
                rowSpan={cell.rowSpan}
                colSpan={cell.colSpan}
            >
                {isEditing && !disabled ? (
                    isHeader ? (
                        <input
                            ref={inputRef as RefObject<HTMLInputElement>}
                            value={draft}
                            onChange={(event) => handleDraftChange(event.target.value)}
                            onBlur={() => handleSave('blur')}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleSave('enter');
                                }
                                if (event.key === 'Escape') {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleCancel();
                                }
                            }}
                            className={cn(
                                'm-0 w-full border-none bg-transparent p-0 text-xs font-semibold leading-[1rem] outline-none',
                                alignmentClass,
                            )}
                            style={{ color: textColor || undefined }}
                        />
                    ) : (
                        <textarea
                            ref={inputRef as RefObject<HTMLTextAreaElement>}
                            value={draft}
                            onChange={(event) => handleDraftChange(event.target.value)}
                            onBlur={() => handleSave('blur')}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' && !event.shiftKey) {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleSave('enter');
                                }
                                if (event.key === 'Escape') {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleCancel();
                                }
                            }}
                            rows={1}
                            className={cn(
                                'm-0 block w-full resize-none overflow-hidden border-none bg-transparent p-0 text-xs font-normal leading-[1rem] outline-none',
                                alignmentClass,
                            )}
                            // Without this the text flips back to the default
                            // ink the moment the cell enters edit mode.
                            style={{ color: textColor || undefined }}
                        />
                    )
                ) : (
                    <div
                        className={cn(
                            'whitespace-pre-wrap break-words',
                            disabled ? 'select-none' : 'cursor-text',
                            alignmentClass,
                        )}
                    >
                        {cell.value ||
                            (!isHeader ? <span className="italic text-slate-300">...</span> : null)}
                    </div>
                )}

                {isLeafHeader && !disabled && onResizeStart && (
                    <div
                        onMouseDown={(event) => {
                            event.stopPropagation();
                            onResizeStart(event);
                        }}
                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400"
                    />
                )}

                {onOpenCalculator && !disabled && !isHeader && !isEditing && (
                    <button
                        onClick={(event) => {
                            event.stopPropagation();
                            onOpenCalculator();
                        }}
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 opacity-0 transition-opacity hover:text-blue-600 group-data-[hover-intent=on]/tbl:group-hover:opacity-100"
                        title="Abrir calculadora"
                        type="button"
                    >
                        <Calculator size={14} />
                    </button>
                )}
            </Component>
        </>
    );
}
